import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { studentAPI, authAPI, batchAPI, modelsAPI, recordAPI, CATEGORIES } from "../services/api";

const EMPTY_STUDENT = { name: "", grade: 1, class_num: 1, number: 1 };
const EMPTY_PW = { current: "", next: "", confirm: "" };
const PAGE_SIZE = 20;

const STATUS_ICON = { pending: "⏳", running: "🔄", done: "✅", failed: "❌", skipped: "⏭" };
const STATUS_COLOR = { done: "#0e9f6e", failed: "#e02424", skipped: "#9ca3af", running: "#1a56db", pending: "#9ca3af" };

function BatchModal({ tabType, onClose }) {
  const [models, setModels] = useState([]);
  const [students, setStudents] = useState([]);
  const [category, setCategory] = useState("autonomous");
  const [stages, setStages] = useState("review1");
  const [r1Model, setR1Model] = useState("");
  const [r2Model, setR2Model] = useState("");
  const [fnModel, setFnModel] = useState("");
  const [selectMode, setSelectMode] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    Promise.all([modelsAPI.list(), studentAPI.list()]).then(([m, s]) => {
      setModels(m.data);
      if (m.data.length > 0) { setR1Model(m.data[0].value); setR2Model(m.data[0].value); setFnModel(m.data[0].value); }
      const filtered = s.data.filter(st => st.tab_type === tabType);
      setStudents(filtered);
      setSelectedIds(new Set(filtered.map(st => st.id)));
    });
    return () => clearInterval(pollRef.current);
  }, [tabType]);

  const toggleStudent = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(selectedIds.size === students.length ? new Set() : new Set(students.map(s => s.id)));
  };

  const stageList = stages === "review1" ? ["review1"] : stages === "review1_2" ? ["review1", "review2"] : ["review1", "review2", "final"];

  const startBatch = async () => {
    setError("");
    if (selectMode === "pick" && selectedIds.size === 0) { setError("처리할 학생을 선택해주세요"); return; }
    try {
      const { data } = await batchAPI.start({
        tab_type: tabType,
        category,
        stages: stageList,
        review1_model: r1Model,
        review2_model: r2Model,
        final_model: fnModel,
        student_ids: selectMode === "pick" ? [...selectedIds] : null,
      });
      setJobId(data.job_id);
      pollRef.current = setInterval(async () => {
        const { data: j } = await batchAPI.status(data.job_id);
        setJob(j);
        if (j.status === "done" || j.status === "cancelled") clearInterval(pollRef.current);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "배치 시작 중 오류 발생");
    }
  };

  const cancelBatch = async () => { if (jobId) await batchAPI.cancel(jobId); };

  const done = job?.status === "done" || job?.status === "cancelled";
  const progress = job ? Math.round(((job.completed + job.failed) / Math.max(job.total - job.skipped, 1)) * 100) : 0;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 28, width: "100%", maxWidth: 580, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,.18)" }}>
        <h3 className="font-bold" style={{ marginBottom: 20, fontSize: "1.05rem" }}>학급 일괄 AI 처리</h3>

        {!jobId ? (
          <>
            {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

            {/* 대상 학생 선택 */}
            <div className="form-group">
              <label>대상 학생</label>
              <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input type="radio" checked={selectMode === "all"} onChange={() => setSelectMode("all")} /> 전체 ({students.length}명)
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input type="radio" checked={selectMode === "pick"} onChange={() => setSelectMode("pick")} /> 직접 선택
                </label>
              </div>
              {selectMode === "pick" && (
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, maxHeight: 200, overflowY: "auto" }}>
                  <div style={{ padding: "6px 12px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 8, background: "#f9fafb" }}>
                    <input type="checkbox" checked={selectedIds.size === students.length && students.length > 0}
                      onChange={toggleAll} id="chk-all" />
                    <label htmlFor="chk-all" style={{ fontSize: ".85rem", cursor: "pointer" }}>
                      전체 선택 ({selectedIds.size}/{students.length})
                    </label>
                  </div>
                  {students.map(s => (
                    <div key={s.id} style={{ padding: "6px 12px", borderBottom: "1px solid #f9fafb", display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="checkbox" id={`chk-${s.id}`} checked={selectedIds.has(s.id)} onChange={() => toggleStudent(s.id)} />
                      <label htmlFor={`chk-${s.id}`} style={{ flex: 1, cursor: "pointer", fontSize: ".88rem" }}>
                        <span style={{ fontWeight: 500 }}>{s.name}</span>
                        <span className="text-gray" style={{ marginLeft: 8, fontSize: ".8rem" }}>{s.grade}학년 {s.class_num}반 {s.number}번</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>항목</label>
              <select className="form-control" value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>실행 단계</label>
              <select className="form-control" value={stages} onChange={(e) => setStages(e.target.value)}>
                <option value="review1">1차 검토만</option>
                <option value="review1_2">1차 + 2차 검토</option>
                <option value="all">1차 + 2차 + 최종 출력 (전체)</option>
              </select>
            </div>
            <div className="form-group">
              <label>1차 검토 모델</label>
              <select className="form-control" value={r1Model} onChange={(e) => setR1Model(e.target.value)}>
                {models.map((m) => <option key={m.value} value={m.value}>{m.label}{m.description ? ` (${m.description})` : ""}</option>)}
              </select>
            </div>
            {stages !== "review1" && (
              <div className="form-group">
                <label>2차 검토 모델</label>
                <select className="form-control" value={r2Model} onChange={(e) => setR2Model(e.target.value)}>
                  {models.map((m) => <option key={m.value} value={m.value}>{m.label}{m.description ? ` (${m.description})` : ""}</option>)}
                </select>
              </div>
            )}
            {stages === "all" && (
              <div className="form-group">
                <label>최종 출력 모델</label>
                <select className="form-control" value={fnModel} onChange={(e) => setFnModel(e.target.value)}>
                  {models.map((m) => <option key={m.value} value={m.value}>{m.label}{m.description ? ` (${m.description})` : ""}</option>)}
                </select>
              </div>
            )}
            <p className="text-sm text-gray" style={{ marginBottom: 16 }}>초안이 없는 학생은 자동으로 건너뜁니다.</p>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" onClick={startBatch} style={{ flex: 1 }} disabled={!r1Model}>실행 시작</button>
              <button className="btn btn-outline" onClick={onClose} style={{ flex: 1 }}>취소</button>
            </div>
          </>
        ) : (
          <>
            {job && (
              <>
                <div style={{ marginBottom: 12 }}>
                  {!done ? (
                    <span style={{ fontWeight: 600, color: "#1a56db" }}>처리 중... {job.completed + job.failed}/{job.total - job.skipped}명</span>
                  ) : job.status === "cancelled" ? (
                    <span style={{ fontWeight: 600, color: "#e02424" }}>취소됨</span>
                  ) : (
                    <span style={{ fontWeight: 600, color: "#0e9f6e" }}>완료</span>
                  )}
                  {!done && <span className="text-sm text-gray" style={{ marginLeft: 8 }}>{progress}%</span>}
                </div>
                {!done && (
                  <div style={{ background: "#e5e7eb", borderRadius: 4, height: 8, marginBottom: 12 }}>
                    <div style={{ background: "#1a56db", borderRadius: 4, height: 8, width: `${progress}%`, transition: "width .3s" }} />
                  </div>
                )}
                {done && (
                  <div className={`alert ${job.failed > 0 ? "alert-error" : "alert-success"}`} style={{ marginBottom: 12 }}>
                    완료 {job.completed}명&nbsp;
                    {job.failed > 0 && <>/ 실패 {job.failed}명&nbsp;</>}
                    {job.skipped > 0 && <>/ 건너뜀 {job.skipped}명</>}
                  </div>
                )}
                <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 6 }}>
                  {job.items.map((item, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", padding: "7px 12px", borderBottom: "1px solid #f3f4f6", gap: 8 }}>
                      <span style={{ fontSize: "1rem" }}>{STATUS_ICON[item.status] || "⏳"}</span>
                      <span style={{ flex: 1, fontWeight: 500 }}>{item.name}</span>
                      <span className="text-sm text-gray">{item.grade}학년 {item.class_num}반 {item.number}번</span>
                      {item.error && <span style={{ fontSize: ".78rem", color: STATUS_COLOR[item.status] }}>{item.error}</span>}
                    </div>
                  ))}
                </div>
              </>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              {!done && <button className="btn btn-danger" onClick={cancelBatch} style={{ flex: 1 }}>취소</button>}
              {done && <button className="btn btn-primary" onClick={onClose} style={{ flex: 1 }}>닫기</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const STATUS_BADGE = {
  none:    { label: "미작성",  bg: "#f3f4f6", color: "#6b7280" },
  draft:   { label: "초안",    bg: "#fef9c3", color: "#854d0e" },
  review1: { label: "1차완료", bg: "#dbeafe", color: "#1e40af" },
  review2: { label: "2차완료", bg: "#e0e7ff", color: "#3730a3" },
  done:    { label: "최종완료", bg: "#d1fae5", color: "#065f46" },
};

const toStatusKey = (status) => {
  if (!status) return "none";
  if (status === "done") return "done";
  if (status === "review2") return "review2";
  if (status === "review1") return "review1";
  return "draft";
};

// 탭별 표시 카테고리 (교과는 subject 동적 열이므로 별도 처리)
const TAB_CATEGORIES = {
  homeroom: [
    { key: "autonomous", label: "자율활동" },
    { key: "career",     label: "진로활동" },
    { key: "behavioral", label: "행동특성" },
  ],
  club: [
    { key: "club", label: "동아리활동" },
  ],
};

function StatusBadge({ status }) {
  const badge = STATUS_BADGE[toStatusKey(status)];
  return (
    <span style={{ background: badge.bg, color: badge.color, padding: "2px 7px", borderRadius: 10, fontSize: ".76rem", fontWeight: 600, whiteSpace: "nowrap" }}>
      {badge.label}
    </span>
  );
}

function StatusDashboard() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState([]);
  const [filterTab, setFilterTab] = useState("homeroom");
  const [filterGrade, setFilterGrade] = useState("all");
  const [filterClass, setFilterClass] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([studentAPI.list(), recordAPI.list()]).then(([s, r]) => {
      setStudents(s.data);
      setRecords(r.data);
      setLoading(false);
    });
  }, []);

  const filteredByTab = students.filter(s => s.tab_type === filterTab);
  const tabStudents = filteredByTab
    .filter(s => filterGrade === "all" || String(s.grade) === filterGrade)
    .filter(s => filterClass === "all" || String(s.class_num) === filterClass);

  const grades = [...new Set(filteredByTab.map(s => s.grade))].sort();
  const classes = [...new Set(
    filteredByTab.filter(s => filterGrade === "all" || String(s.grade) === filterGrade).map(s => s.class_num)
  )].sort((a, b) => a - b);

  // 담임·동아리: 카테고리별 맵
  const catMap = {};
  records.forEach(r => {
    if (!catMap[r.student_id]) catMap[r.student_id] = {};
    catMap[r.student_id][r.category] = r.status;
  });

  // 교과: subject별 맵 + 열 목록
  const subjectMap = {};
  const subjectSet = new Set();
  records
    .filter(r => r.category === "subject_detail" && r.subject)
    .forEach(r => {
      if (!subjectMap[r.student_id]) subjectMap[r.student_id] = {};
      subjectMap[r.student_id][r.subject] = r.status;
      subjectSet.add(r.subject);
    });
  const subjectCols = [...subjectSet].sort();

  const isSubject = filterTab === "subject";
  const catCols = TAB_CATEGORIES[filterTab] || [];
  const colCount = 2 + (isSubject ? subjectCols.length : catCols.length);

  if (loading) return <div className="card" style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>불러오는 중...</div>;

  return (
    <div className="card">
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 4 }}>
          {[["homeroom","담임"],["subject","교과"],["club","동아리"]].map(([t, l]) => (
            <button key={t} className={`btn ${filterTab === t ? "btn-primary" : "btn-outline"}`}
              style={{ padding: "4px 12px", fontSize: ".85rem" }}
              onClick={() => { setFilterTab(t); setFilterGrade("all"); setFilterClass("all"); }}>
              {l}
            </button>
          ))}
        </div>
        <select className="form-control" style={{ width: "auto" }} value={filterGrade}
          onChange={e => { setFilterGrade(e.target.value); setFilterClass("all"); }}>
          <option value="all">전체 학년</option>
          {grades.map(g => <option key={g} value={g}>{g}학년</option>)}
        </select>
        <select className="form-control" style={{ width: "auto" }} value={filterClass}
          onChange={e => setFilterClass(e.target.value)}>
          <option value="all">전체 반</option>
          {classes.map(c => <option key={c} value={c}>{c}반</option>)}
        </select>
        <span className="text-sm text-gray">{tabStudents.length}명</span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="table" style={{ fontSize: ".82rem", minWidth: 400 }}>
          <thead>
            <tr>
              <th>학년/반/번호</th>
              <th>이름</th>
              {isSubject
                ? subjectCols.length > 0
                  ? subjectCols.map(s => <th key={s} style={{ textAlign: "center" }}>{s}</th>)
                  : <th style={{ textAlign: "center" }}>세특 과목</th>
                : catCols.map(c => <th key={c.key} style={{ textAlign: "center" }}>{c.label}</th>)
              }
            </tr>
          </thead>
          <tbody>
            {tabStudents.length === 0 && (
              <tr><td colSpan={colCount} style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}>해당하는 학생이 없습니다</td></tr>
            )}
            {isSubject && subjectCols.length === 0 && tabStudents.length > 0 && (
              <tr><td colSpan={3} style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}>등록된 세특 과목이 없습니다</td></tr>
            )}
            {tabStudents.map(s => (
              <tr key={s.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/student/${s.id}`)}>
                <td className="text-gray" style={{ whiteSpace: "nowrap" }}>{s.grade}학년 {s.class_num}반 {s.number}번</td>
                <td className="font-bold" style={{ color: "#1a56db", whiteSpace: "nowrap" }}>{s.name}</td>
                {isSubject
                  ? subjectCols.map(subj => (
                      <td key={subj} style={{ textAlign: "center" }}>
                        <StatusBadge status={subjectMap[s.id]?.[subj]} />
                      </td>
                    ))
                  : catCols.map(c => (
                      <td key={c.key} style={{ textAlign: "center" }}>
                        <StatusBadge status={catMap[s.id]?.[c.key]} />
                      </td>
                    ))
                }
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
        {Object.values(STATUS_BADGE).map(b => (
          <span key={b.label} style={{ background: b.bg, color: b.color, padding: "2px 8px", borderRadius: 10, fontSize: ".78rem", fontWeight: 600 }}>{b.label}</span>
        ))}
      </div>
    </div>
  );
}

function StudentPanel({ tabType }) {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [newStudent, setNewStudent] = useState(EMPTY_STUDENT);
  const [editingStudent, setEditingStudent] = useState(null);
  const [error, setError] = useState("");
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [showBatch, setShowBatch] = useState(false);
  const [filterGrade, setFilterGrade] = useState("all");
  const [filterClass, setFilterClass] = useState("all");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    const { data } = await studentAPI.list();
    setStudents(data.filter((s) => s.tab_type === tabType));
    setPage(1);
  }, [tabType]);

  useEffect(() => { load(); }, [load]);

  const addStudent = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await studentAPI.create({
        name: newStudent.name,
        grade: Number(newStudent.grade),
        class_num: Number(newStudent.class_num),
        number: Number(newStudent.number),
        tab_type: tabType,
      });
      setNewStudent(EMPTY_STUDENT);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "오류 발생");
    }
  };

  const startEdit = (s) => {
    setEditingStudent({ id: s.id, name: s.name, grade: s.grade, class_num: s.class_num, number: s.number });
    setError("");
  };

  const saveEdit = async () => {
    setError("");
    try {
      await studentAPI.update(editingStudent.id, {
        name: editingStudent.name,
        grade: Number(editingStudent.grade),
        class_num: Number(editingStudent.class_num),
        number: Number(editingStudent.number),
        tab_type: tabType,
      });
      setEditingStudent(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "수정 중 오류 발생");
    }
  };

  const deleteStudent = async (id) => {
    if (!window.confirm("학생을 삭제하시겠습니까? 해당 학생의 모든 생활기록부도 삭제됩니다.")) return;
    try {
      await studentAPI.delete(id);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "삭제 중 오류 발생");
    }
  };

  const label = tabType === "homeroom" ? "담임" : tabType === "subject" ? "교과" : "동아리";

  const grades = [...new Set(students.map(s => s.grade))].sort();
  const classes = [...new Set(
    students.filter(s => filterGrade === "all" || String(s.grade) === filterGrade).map(s => s.class_num)
  )].sort((a, b) => a - b);

  const filtered = students
    .filter(s => filterGrade === "all" || String(s.grade) === filterGrade)
    .filter(s => filterClass === "all" || String(s.class_num) === filterClass);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleGradeChange = (val) => { setFilterGrade(val); setFilterClass("all"); setPage(1); setEditingStudent(null); };
  const handleClassChange = (val) => { setFilterClass(val); setPage(1); setEditingStudent(null); };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setCsvResult(null);
    setCsvUploading(true);
    setError("");
    try {
      const { data } = await studentAPI.bulk(file, tabType);
      setCsvResult(data);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "CSV 업로드 중 오류 발생");
    } finally {
      setCsvUploading(false);
    }
  };

  return (
    <>
      {showBatch && <BatchModal tabType={tabType} onClose={() => setShowBatch(false)} />}
      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button className="btn btn-primary" onClick={() => setShowBatch(true)}>일괄 AI 처리</button>
      </div>

      <div className="card">
        <h3 className="font-bold mb-2">학생 등록 ({label})</h3>
        <form onSubmit={addStudent} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="form-group" style={{ flex: "1 1 70px", marginBottom: 0 }}>
            <label>학년</label>
            <select className="form-control" value={newStudent.grade}
              onChange={(e) => setNewStudent({ ...newStudent, grade: e.target.value })}>
              {[1, 2, 3].map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex: "1 1 60px", marginBottom: 0 }}>
            <label>반</label>
            <input className="form-control" type="number" min={1} max={20} value={newStudent.class_num}
              onChange={(e) => setNewStudent({ ...newStudent, class_num: e.target.value })} />
          </div>
          <div className="form-group" style={{ flex: "1 1 60px", marginBottom: 0 }}>
            <label>번호</label>
            <input className="form-control" type="number" min={1} max={40} value={newStudent.number}
              onChange={(e) => setNewStudent({ ...newStudent, number: e.target.value })} />
          </div>
          <div className="form-group" style={{ flex: "3 1 160px", marginBottom: 0 }}>
            <label>이름</label>
            <input className="form-control" value={newStudent.name}
              onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })} required />
          </div>
          <button className="btn btn-primary" type="submit" style={{ marginBottom: 0 }}>등록</button>
        </form>

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span className="text-sm text-gray">CSV 일괄 등록</span>
          <label style={{ cursor: "pointer" }}>
            <input type="file" accept=".csv" style={{ display: "none" }} onChange={handleCsvUpload} disabled={csvUploading} />
            <span className="btn btn-outline" style={{ padding: "4px 12px", fontSize: ".85rem", pointerEvents: csvUploading ? "none" : "auto", opacity: csvUploading ? 0.6 : 1 }}>
              {csvUploading ? "업로드 중..." : "CSV 파일 선택"}
            </span>
          </label>
          <span className="text-sm text-gray">헤더: 학년,반,번호,이름 · 최대 200명</span>
        </div>

        {csvResult && (
          <div style={{ marginTop: 10 }}>
            <div className={`alert ${csvResult.failed.length === 0 ? "alert-success" : "alert-error"}`} style={{ marginBottom: csvResult.failed.length > 0 ? 8 : 0 }}>
              {csvResult.success}명 등록 완료{csvResult.failed.length > 0 ? ` / ${csvResult.failed.length}건 실패` : ""}
            </div>
            {csvResult.failed.length > 0 && (
              <table className="table" style={{ fontSize: ".82rem" }}>
                <thead><tr><th>행</th><th>이름</th><th>학년·반·번호</th><th>실패 사유</th></tr></thead>
                <tbody>
                  {csvResult.failed.map((f) => (
                    <tr key={f.row}>
                      <td>{f.row}</td>
                      <td>{f.data["이름"] || "-"}</td>
                      <td>{f.data["학년"]}학년 {f.data["반"]}반 {f.data["번호"]}번</td>
                      <td style={{ color: "#e02424" }}>{f.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <div className="card">
        {/* 필터 바 */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
          <select className="form-control" style={{ width: "auto" }} value={filterGrade} onChange={e => handleGradeChange(e.target.value)}>
            <option value="all">전체 학년</option>
            {grades.map(g => <option key={g} value={g}>{g}학년</option>)}
          </select>
          <select className="form-control" style={{ width: "auto" }} value={filterClass} onChange={e => handleClassChange(e.target.value)}>
            <option value="all">전체 반</option>
            {classes.map(c => <option key={c} value={c}>{c}반</option>)}
          </select>
          <span className="text-sm text-gray">
            {filtered.length}명
            {(filterGrade !== "all" || filterClass !== "all") && (
              <button className="btn btn-outline" style={{ marginLeft: 8, padding: "2px 8px", fontSize: ".78rem" }}
                onClick={() => { handleGradeChange("all"); }}>
                초기화
              </button>
            )}
          </span>
        </div>

        <table className="table">
          <thead>
            <tr><th>이름</th><th>학년 / 반 / 번호</th><th>관리</th></tr>
          </thead>
          <tbody>
            {students.length === 0 && (
              <tr><td colSpan={3} style={{ textAlign: "center", color: "#9ca3af", padding: 24 }}>등록된 학생이 없습니다</td></tr>
            )}
            {students.length > 0 && filtered.length === 0 && (
              <tr><td colSpan={3} style={{ textAlign: "center", color: "#9ca3af", padding: 24 }}>필터 조건에 맞는 학생이 없습니다</td></tr>
            )}
            {paginated.map((s) => editingStudent?.id === s.id ? (
              <tr key={s.id} style={{ background: "#f0f9ff" }}>
                <td>
                  <input className="form-control" value={editingStudent.name}
                    onChange={(e) => setEditingStudent({ ...editingStudent, name: e.target.value })}
                    style={{ padding: "4px 8px" }} />
                </td>
                <td style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <select className="form-control" value={editingStudent.grade}
                    onChange={(e) => setEditingStudent({ ...editingStudent, grade: e.target.value })}
                    style={{ padding: "4px 8px" }}>
                    {[1, 2, 3].map((g) => <option key={g}>{g}</option>)}
                  </select>
                  <span>학년</span>
                  <input className="form-control" type="number" min={1} max={20} value={editingStudent.class_num}
                    onChange={(e) => setEditingStudent({ ...editingStudent, class_num: e.target.value })}
                    style={{ padding: "4px 8px", width: 55 }} />
                  <span>반</span>
                  <input className="form-control" type="number" min={1} max={40} value={editingStudent.number}
                    onChange={(e) => setEditingStudent({ ...editingStudent, number: e.target.value })}
                    style={{ padding: "4px 8px", width: 55 }} />
                  <span>번</span>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="btn btn-primary" style={{ padding: "4px 10px", fontSize: ".82rem", marginRight: 4 }} onClick={saveEdit}>저장</button>
                  <button className="btn btn-outline" style={{ padding: "4px 10px", fontSize: ".82rem" }} onClick={() => setEditingStudent(null)}>취소</button>
                </td>
              </tr>
            ) : (
              <tr key={s.id}>
                <td className="font-bold" style={{ cursor: "pointer", color: "#1a56db" }}
                  onClick={() => navigate(`/student/${s.id}`)}>
                  {s.name}
                </td>
                <td>{s.grade}학년 {s.class_num}반 {s.number}번</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="btn btn-success" style={{ padding: "4px 10px", fontSize: ".82rem", marginRight: 4 }} onClick={() => navigate(`/student/${s.id}`)}>생기부 관리</button>
                  <button className="btn btn-outline" style={{ padding: "4px 10px", fontSize: ".82rem", marginRight: 4 }} onClick={() => startEdit(s)}>수정</button>
                  <button className="btn btn-danger" style={{ padding: "4px 10px", fontSize: ".82rem" }} onClick={() => deleteStudent(s.id)}>삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4, marginTop: 14, flexWrap: "wrap" }}>
            <button className="btn btn-outline" style={{ padding: "4px 10px", fontSize: ".82rem" }}
              disabled={page === 1} onClick={() => setPage(1)}>처음</button>
            <button className="btn btn-outline" style={{ padding: "4px 10px", fontSize: ".82rem" }}
              disabled={page === 1} onClick={() => setPage(p => p - 1)}>이전</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) => p === "..." ? (
                <span key={`dot-${i}`} style={{ padding: "0 4px", color: "#9ca3af" }}>...</span>
              ) : (
                <button key={p} className={`btn ${p === page ? "btn-primary" : "btn-outline"}`}
                  style={{ padding: "4px 10px", fontSize: ".82rem", minWidth: 36 }}
                  onClick={() => setPage(p)}>{p}</button>
              ))
            }
            <button className="btn btn-outline" style={{ padding: "4px 10px", fontSize: ".82rem" }}
              disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>다음</button>
            <button className="btn btn-outline" style={{ padding: "4px 10px", fontSize: ".82rem" }}
              disabled={page === totalPages} onClick={() => setPage(totalPages)}>마지막</button>
            <span className="text-sm text-gray" style={{ marginLeft: 4 }}>{page} / {totalPages} 페이지</span>
          </div>
        )}
      </div>
    </>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const teacher = JSON.parse(localStorage.getItem("teacher") || "{}");

  const [tab, setTab] = useState("homeroom");
  const [showPwModal, setShowPwModal] = useState(false);
  const [pw, setPw] = useState(EMPTY_PW);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  const handleLogout = () => { localStorage.clear(); navigate("/login"); };
  const openPwModal = () => { setPw(EMPTY_PW); setPwError(""); setPwSuccess(false); setShowPwModal(true); };
  const closePwModal = () => setShowPwModal(false);

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    setPwError("");
    if (pw.next !== pw.confirm) { setPwError("새 비밀번호가 일치하지 않습니다"); return; }
    if (pw.next.length < 6) { setPwError("새 비밀번호는 6자 이상이어야 합니다"); return; }
    setPwLoading(true);
    try {
      await authAPI.changePassword(pw.current, pw.next);
      setPwSuccess(true);
      setPw(EMPTY_PW);
    } catch (err) {
      setPwError(err.response?.data?.detail || "비밀번호 변경 중 오류 발생");
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <>
      {/* 비밀번호 변경 모달 */}
      {showPwModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 32, width: "100%", maxWidth: 400, boxShadow: "0 8px 32px rgba(0,0,0,.18)" }}>
            <h3 className="font-bold" style={{ marginBottom: 20, fontSize: "1.1rem" }}>비밀번호 변경</h3>
            {pwSuccess ? (
              <>
                <div className="alert alert-success">비밀번호가 성공적으로 변경되었습니다.</div>
                <button className="btn btn-primary" style={{ width: "100%", marginTop: 8 }} onClick={closePwModal}>닫기</button>
              </>
            ) : (
              <form onSubmit={submitPasswordChange}>
                {pwError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{pwError}</div>}
                <div className="form-group">
                  <label>현재 비밀번호</label>
                  <input className="form-control" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>새 비밀번호</label>
                  <input className="form-control" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="6자 이상" required />
                </div>
                <div className="form-group">
                  <label>새 비밀번호 확인</label>
                  <input className="form-control" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <button className="btn btn-primary" type="submit" disabled={pwLoading} style={{ flex: 1 }}>
                    {pwLoading ? <><span className="spinner" />변경 중...</> : "변경"}
                  </button>
                  <button className="btn btn-outline" type="button" onClick={closePwModal} style={{ flex: 1 }}>취소</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <nav>
        <div className="nav-inner">
          <h1>생활기록부 자동화 시스템</h1>
          <div className="nav-right">
            <span>{teacher.full_name} ({teacher.school})</span>
            <button onClick={openPwModal}>비밀번호 변경</button>
            <button onClick={handleLogout}>로그아웃</button>
          </div>
        </div>
      </nav>

      <div className="container" style={{ marginTop: 28 }}>
        <div className="tabs">
          <button className={`tab ${tab === "homeroom" ? "active" : ""}`} onClick={() => setTab("homeroom")}>담임교사</button>
          <button className={`tab ${tab === "subject" ? "active" : ""}`} onClick={() => setTab("subject")}>교과교사</button>
          <button className={`tab ${tab === "club" ? "active" : ""}`} onClick={() => setTab("club")}>동아리교사</button>
          <button className={`tab ${tab === "status" ? "active" : ""}`} onClick={() => setTab("status")}>완성 현황</button>
        </div>

        {tab === "status" ? <StatusDashboard /> : <StudentPanel key={tab} tabType={tab} />}
      </div>
    </>
  );
}
